import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { CompanyIdIssuanceInfo, MailInfo } from "IF/interfaceType";
import { API_BASE_URL, RESPONSE_CODE } from "Utils/apiUtil";
import axios from "axios";
import { MessageModal } from "./MessageModal";
import { UserRole, useSessionStorage } from "IF/userSession";
import SettingNewMailDialog from "./SettingNewMailDialog";
import ConfirmationModal from "./ConfirmationModal";
import SettingMailMobile from "./SettingMailMobile";
import SettingMailRow from "./SettingMailRow";
import { DataContext } from "Contexts/DataProvider";
import { createTitle } from "Utils/format";
// 各列の幅割合（合計100%）
export const mailColumnWidths: Record<string, string> = {
  mailName: "15%",
  mailAddress: "20%",
  alertCheckbox: "10%",
  connectCheckbox: "10%",
  telNumber: "15%",
  smsCheckbox: "10%",
  action: "15%",
};
//認証ダイアログ
export type VerificationType = "email" | "phone";
export const SettingMail = () => {
  // ユーザ情報
  const { getUserData } = useSessionStorage();
  const userData = getUserData();
  const { id: userId, companyCode, userRole } = userData;
  // 新規登録モーダル開閉
  const [open, setOpen] = useState(false);

  // サイドバーデータ持ち回り
  const sidebarContext = useContext(DataContext);
  if (!sidebarContext) {
    throw new Error("ChildComponent must be used within an ViewProvider");
  }
  const { menuItems, setChoiceMenu, toggleMenu } = sidebarContext;

  // 確認モーダル
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const handleConfirmModalClose = () => setConfirmModalOpen(false);

  const handleConfirmModalOpen = (
    message: string,
    confirmAction: () => void
  ) => {
    setConfirmMessage(message);
    setOnConfirm(() => confirmAction);
    setConfirmModalOpen(true);
  };

  // 登録データ作成
  const insertInitData: MailInfo = {
    mailName: "",
    mailAddress: "",
    alertCheckbox: false,
    connectCheckbox: false,
    telNumber: "",
    smsCheckbox: false,
    createUser: userId,
    createDatetime: new Date(),
    updateUser: userId,
    updateDatetime: new Date(),
  };
  // 登録用メールアドレス
  const [newEmail, setNewEmail] = useState<MailInfo>(insertInitData);
  // レイアウト設定
  const [tableBodyHeight, setTableBodyHeight] = useState(600);
  const [cardBodyWidth, setCardBodyWidth] = useState(0);
  // モーダル
  const [isModalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");
  const closeModal = () => {
    setModalOpen(false);
    setMessage("");
  };

  // 編集管理
  const [editingRows, setEditingRows] = useState<Set<number>>(new Set());
  const [rowEditData, setRowEditData] = useState<Record<number, MailInfo>>({});

  // 配信一覧データ
  const [mailInfos, setMailInfos] = useState<MailInfo[]>([]);
  // 初期配信一覧データ
  const [originalData, setOriginalData] = useState<MailInfo[]>([]);
  // 配信可能人数
  const [limit, setLimit] = useState<CompanyIdIssuanceInfo>();

  const relevantMailInfos = useMemo(() => {
    if (mailInfos.length === 0) {
      return [];
    }
    return mailInfos.filter(
      (row) => row.mailAddress !== null && row.mailAddress !== ""
    );
  }, [mailInfos]);

  const relevantTelInfos = useMemo(() => {
    return mailInfos.filter(
      (row) => row.telNumber !== null && row.telNumber !== ""
    );
  }, [mailInfos]);

  const isAllAlertOnlyChecked = useMemo(() => {
    if (relevantMailInfos.length === 0) {
      return false;
    }
    return relevantMailInfos.every((row) => row.alertCheckbox);
  }, [relevantMailInfos]);

  const isSomeAlertOnlyChecked = useMemo(() => {
    return relevantMailInfos.some((row) => row.alertCheckbox);
  }, [relevantMailInfos]);

  const isAllConnectChecked = useMemo(() => {
    if (relevantMailInfos.length === 0) {
      return false;
    }
    return relevantMailInfos.every((row) => row.connectCheckbox);
  }, [relevantMailInfos]);

  const isSomeConnectChecked = useMemo(() => {
    return relevantMailInfos.some((row) => row.connectCheckbox);
  }, [relevantMailInfos]);

  const isAllSmsChecked = useMemo(() => {
    if (relevantTelInfos.length === 0) {
      return false;
    }
    return relevantTelInfos.every((row) => row.smsCheckbox);
  }, [relevantTelInfos]);

  const isSomeSmsChecked = useMemo(() => {
    return relevantTelInfos.some((row) => row.smsCheckbox);
  }, [relevantTelInfos]);

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("lg"));

  // 初期化
  const init = () => {
    setEditingRows(new Set());
    setRowEditData({});
    getData(companyCode);
    setDialogMessage("");
  };

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 3 : 4;
      document.title = createTitle(menuItems[index]?.subItems?.[2]?.text ?? "");
      setChoiceMenu(menuItems[index]?.subItems?.[2]?.text ?? "");
      toggleMenu("設定");
      if (UserRole.YKC === userRole) {
        toggleMenu(menuItems[index]?.subItems?.[2]?.text ?? "");
      }
    }
  }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

  // タイトル設定
  useEffect(() => {
    if (menuItems.length > 2) {
      const index = UserRole.YKC !== userRole ? 3 : 4;
      document.title = createTitle(menuItems[index]?.subItems?.[3]?.text ?? "");
      setChoiceMenu(menuItems[index]?.subItems?.[3]?.text ?? "");
      toggleMenu("設定");
      if (UserRole.YKC === userRole) {
        toggleMenu(menuItems[index]?.subItems?.[3]?.text ?? "");
      }
    }
  }, [userRole, menuItems, setChoiceMenu, toggleMenu]);

  // 初期表示時処理
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight - 150;
      const windowWidth = window.innerWidth - 55;
      setTableBodyHeight(windowHeight);
      setCardBodyWidth(windowWidth);
    };

    // 初期表示時とリサイズ時に高さを計算
    handleResize();
    window.addEventListener("resize", handleResize);

    // 初期表示データ取得
    getData(companyCode);
    getLimit(companyCode);

    setEditingRows(new Set());
    setRowEditData({});

    // クリーンアップ
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [companyCode]);

  // 初期表示時のデータ取得処理
  const getData = async (schema: string) => {
    try {
      // API呼び出し
      const response = await axios.get(`${API_BASE_URL}/MailSetting/GetMail`, {
        params: { schema: schema },
      });
      setMailInfos(response.data);
      setOriginalData(response.data);
    } catch (error) {
      setModalOpen(true);
      setMessage("配信情報の取得に失敗しました");
      console.error("配信情報の取得に失敗しました:", error);
    }
  };

  // 配信可能人数取得
  const getLimit = async (schema: string) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/MailSetting/GetCompanyInfo`,
        {
          params: { companyCode: schema },
        }
      );
      setLimit(response.data);
    } catch (error) {
      setModalOpen(true);
      setMessage("配信可能人数の取得に失敗しました");
      console.error("配信可能人数の取得に失敗しました:", error);
    }
  };

  // 新規登録モーダルの開閉
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setNewEmail(insertInitData);
    setIsEmailVerified(false);
    setIsPhoneVerified(false);
    setDialogMessage("");
  };

  // 必須チェック
  function validateRequired(checkData: MailInfo): boolean {
    let message: string = "";
    if (!checkData.mailName) {
      message += "氏名を入力してください。\n";
    }

    if (!checkData.mailAddress && !checkData.telNumber) {
      message += "メールアドレスまたは電話番号を入力してください。";
    }

    if (message) {
      setModalOpen(true);
      setMessage(message);
      return false;
    }
    return true;
  }

  // 編集開始
  const startEditingRow = (index: number) => {
    setRowEditData((prev) => ({
      ...prev,
      [index]: { ...mailInfos[index], updateUser: userId },
    }));
    setEditingRows((prev) => new Set(prev).add(index));
  };

  // 編集停止
  const stopEditingRow = (index: number) => {
    setEditingRows((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
    setRowEditData((prev) => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  // 編集中のデータ変更
  const handleChange = (
    index: number,
    field: keyof MailInfo,
    checked: string | boolean
  ) => {
    setRowEditData((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: checked,
        updateUser: userId,
      },
    }));

    setMailInfos((prevMailInfos) => {
      return prevMailInfos.map((mail, i) => {
        if (i === index) {
          return {
            ...mail,
            [field]: checked,
            updateUser: userId,
          };
        }
        return mail;
      });
    });
  };

  const getMergedMailInfos = () => {
    return mailInfos.map((info, i) => {
      // rowEditData[i] があればそちらを優先して合体
      return rowEditData[i] ? { ...info, ...rowEditData[i] } : info;
    });
  };

  // 新規登録の配信チェックボックスの変更
  const newMailCheckboxChange =
    (field: keyof MailInfo) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const smsCheckedCount = originalData.filter((m) => m.smsCheckbox).length;
      const mailCheckedCount = originalData.filter(
        (m) => m.alertCheckbox || m.connectCheckbox
      ).length;

      // SMSチェック
      if (
        field === "smsCheckbox" &&
        limit &&
        smsCheckedCount + 1 > limit.smsSendLimit
      ) {
        setDialogMessage(
          `SMS配信可能人数（${limit.smsSendLimit}人）の上限に達しています。`
        );
        return;
      }

      // メールチェック
      if (
        (field === "alertCheckbox" || field === "connectCheckbox") &&
        limit &&
        mailCheckedCount + 1 > limit.mailSendLimit
      ) {
        setDialogMessage(
          `メール配信可能人数（${limit.mailSendLimit}人）の上限に達しています。`
        );
        return;
      }

      // 問題なければメッセージをクリア
      setDialogMessage("");
      setNewEmail((prev) => ({
        ...prev,
        [field]: checked,
      }));
      return;
    };

  // チェックボックスの変更
  const handleCheckboxChange =
    (index: number, field: keyof MailInfo) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;

      const merged = getMergedMailInfos();

      merged[index] = {
        ...merged[index],
        [field]: checked,
        updateUser: userId,
      };

      let isOver = false;

      // SMSの配信可能人数チェック
      if (field === "smsCheckbox" && checked) {
        // smsCheckbox===true の数
        const smsCheckedCount = merged.filter((m) => m.smsCheckbox).length;

        if (limit && smsCheckedCount > limit.smsSendLimit) {
          setModalOpen(true);
          setMessage(`SMS配信可能人数は ${limit.smsSendLimit} 名です`);
          isOver = true;
        }
      }

      // 2) メールの配信可能人数チェック
      if (
        (field === "alertCheckbox" || field === "connectCheckbox") &&
        checked
      ) {
        // alert  / connect のいずれかが true のレコード数
        const mailCheckedCount = merged.filter(
          (m) => m.alertCheckbox || m.connectCheckbox
        ).length;

        if (limit && mailCheckedCount > limit.mailSendLimit) {
          setModalOpen(true);
          setMessage(`メール配信可能人数は ${limit.mailSendLimit} 名です`);
          isOver = true;
        }
      }

      if (!isOver) {
        handleChange(index, field, checked);
      }
    };

  // 新規登録処理
  const handleAddEmail = async () => {
    // 入力チェック
    if (!validateRequired(newEmail)) return;
    try {
      // API呼び出し
      const response = await axios.post(
        `${API_BASE_URL}/MailSetting/PostMail`,
        {
          schema: companyCode,
          mailData: newEmail,
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("配信情報が登録されました。");
        handleClose();
        init();
      } else {
        throw new Error();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("配信情報の登録に失敗しました。");
        console.error("配信情報の登録に失敗しました。", error);
      }
    }
  };

  // キャンセル処理
  const handleCancelRow = (index: number) => {
    stopEditingRow(index);
  };

  // 初期データと変更後formを比較
  const getModifiedElements = (List: MailInfo[]) => {
    if (originalData && List) {
      return List.filter((current, index) => {
        const original: MailInfo = originalData[index];
        return (
          current.alertCheckbox !== original?.alertCheckbox ||
          current.connectCheckbox !== original?.connectCheckbox ||
          current.smsCheckbox !== original.smsCheckbox
        );
      });
    } else {
      return [];
    }
  };

  // 更新処理
  const handleSaveRow = async (index: number) => {
    const updatedRow = rowEditData[index];
    const modifiedElements = getModifiedElements([updatedRow]);
    // 変更なしの場合更新しない
    if (modifiedElements.length === 0) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    try {
      // API呼び出し
      const response = await axios.put(`${API_BASE_URL}/MailSetting/PutMail`, {
        schema: companyCode,
        mailDataList: modifiedElements,
      });
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("配信情報が更新されました。");
      } else {
        throw new Error();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("配信情報の更新に失敗しました。");
        console.error("配信情報の更新に失敗しました。", error);
      }
    }
    stopEditingRow(index);
    getData(companyCode);
  };

  // 反映ボタン押下ハンドラ（複数行一括反映）
  const handleReflectAll = async () => {
    const updatedRows: MailInfo[] = [];
    for (const i of editingRows) {
      updatedRows.push(rowEditData[i]);
    }
    const modifiedElements = getModifiedElements(updatedRows);
    // 変更なしの場合更新しない
    if (modifiedElements.length === 0) {
      setModalOpen(true);
      setMessage("変更がありません。");
      return;
    }
    try {
      // API呼び出し
      const response = await axios.put(`${API_BASE_URL}/MailSetting/PutMail`, {
        schema: companyCode,
        mailDataList: modifiedElements,
      });
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("配信情報が更新されました。");
      } else {
        throw new Error();
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("配信情報の更新に失敗しました。");
        console.error("配信情報の更新に失敗しました。", error);
      }
    }
    init();
  };

  // 削除処理
  const handleDelete = async (index: number) => {
    const deleteData = mailInfos[index];
    try {
      // API呼び出し
      const response = await axios.delete(
        `${API_BASE_URL}/MailSetting/DeleteMail`,
        {
          headers: { "Content-Type": "application/json" },
          data: {
            schema: companyCode,
            mailData: deleteData,
          },
        }
      );
      if (response.status === RESPONSE_CODE.SUCCESS) {
        setModalOpen(true);
        setMessage("配信情報が削除されました。");
      }
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("配信情報の削除に失敗しました。");
        console.error("配信情報の削除に失敗しました。", error);
      }
    }
    stopEditingRow(index);
    init();
  };

  // 全てチェック/チェック解除のハンドラ
  const handleCheckAll = (field: keyof MailInfo, checked: boolean) => {
    let updated;

    // メール配信の場合はメールアドレス、SMS配信は電話番号が登録されている場合のみチェック
    updated = mailInfos.map((row) => {
      let newFieldCheckedState = row[field];

      if (field === "alertCheckbox" || field === "connectCheckbox") {
        if (row.mailAddress !== null && row.mailAddress !== "") {
          newFieldCheckedState = checked ? true : false;
        }
      } else if (field === "smsCheckbox") {
        if (row.telNumber !== null && row.telNumber !== "") {
          newFieldCheckedState = checked ? true : false;
        }
      }

      return {
        ...row,
        [field]: newFieldCheckedState,
        updateUser: userId,
      };
    });

    // SMSの配信可能人数チェック
    if (field === "smsCheckbox" && checked) {
      // smsCheckbox===true の数
      const smsCheckedCount = updated.filter((m) => m.smsCheckbox).length;

      if (limit && smsCheckedCount > limit.smsSendLimit) {
        setModalOpen(true);
        setMessage(`SMS配信可能人数は ${limit.smsSendLimit} 名です`);
        return;
      }
    }

    // 2) メールの配信可能人数チェック
    if ((field === "alertCheckbox" || field === "connectCheckbox") && checked) {
      // alert  / connect のいずれかが true のレコード数
      const mailCheckedCount = updated.filter(
        (m) => m.alertCheckbox || m.connectCheckbox
      ).length;

      if (limit && mailCheckedCount > limit.mailSendLimit) {
        setModalOpen(true);
        setMessage(`メール配信可能人数は ${limit.mailSendLimit} 名です`);
        return;
      }
    }

    setMailInfos(updated);

    // 編集した行を編集モードに設定
    const newRowEditData: Record<number, MailInfo> = {};
    updated.forEach((row, i) => {
      newRowEditData[i] = { ...row };
    });
    setRowEditData(newRowEditData);
    setEditingRows(new Set(updated.map((_, i) => i)));
  };

  const createButton = () => {
    return (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: { xs: "flex-start", md: "flex-end" },
          mb: { xs: 2, lg: 0 },
          mt: { xs: 0, lg: 2 },
        }}
      >
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{
            width: {
              xs: editingRows.size > 0 ? "50%" : "100%",
              sm: editingRows.size > 0 ? "50%" : "100%",
              md: editingRows.size > 0 ? "50%" : "100%",
              lg: "20vh",
            },
          }}
        >
          新規登録
        </Button>
        {editingRows.size > 0 && (
          <Button
            variant="contained"
            style={{ backgroundColor: "#000080" }}
            sx={{
              width: {
                xs: "50%",
                sm: "50%",
                md: "50%",
                lg: "20vh",
              },
            }}
            onClick={handleReflectAll}
          >
            反映
          </Button>
        )}
      </Box>
    );
  };

  // 認証ダイアログの状態
  const [dialogOpen, setDialogOpen] = useState(false);
  const [verificationType, setVerificationType] =
    useState<VerificationType | null>(null);
  const [verificationCode, setVerificationCode] = useState("");

  // 認証済みかどうかの状態
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);

  // 認証ボタン押下時の共通処理
  const handleVerify = async (type: VerificationType, contact: string) => {
    if (type === "email") {
      const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
      if (!emailRegex.test(contact)) {
        alert("メールアドレスの形式が正しくありません。");
        return;
      }
    }
    await issueVerificationCode(type, contact);
  };

  // 認証コード発行API呼び出し
  const issueVerificationCode = async (
    type: "email" | "phone",
    contact: string
  ) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/MailSetting/issue`, {
        schema: companyCode,
        type,
        contact,
      });
      setVerificationType(type);
      setDialogOpen(true);
      console.log("認証コード発行成功:", response.data);
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("認証コードの発行に失敗しました。");
        console.error("認証コードの発行に失敗しました。", error);
      }
    }
  };

  // 認証コード入力ダイアログを閉じる
  const handleDialogClose = () => {
    setDialogOpen(false);
    setVerificationCode("");
    setVerificationType(null);
  };

  // 認証コード検証API呼び出し
  const handleSubmitVerification = async () => {
    try {
      if (verificationType === "email") {
        // メール認証コード検証用のAPI呼び出し
        const response = await axios.post(
          `${API_BASE_URL}/MailSetting/Verify`,
          {
            schema: companyCode,
            type: verificationType,
            contact: newEmail.mailAddress,
            code: verificationCode,
          }
        );
        console.log("メール認証成功: ", response.data);
        setModalOpen(true);
        setMessage("メールアドレスの認証に成功しました");
        setIsEmailVerified(true);
      } else if (verificationType === "phone") {
        // 電話番号認証コード検証用のAPI呼び出し
        const response = await axios.post(
          `${API_BASE_URL}/MailSetting/verify`,
          {
            schema: companyCode,
            type: verificationType,
            contact: newEmail.telNumber,
            code: verificationCode,
          }
        );
        console.log("電話番号認証成功: ", response.data);
        setModalOpen(true);
        setMessage("電話番号認証に成功しました");
        setIsPhoneVerified(true);
      }
      // ダイアログを閉じる
      handleDialogClose();
    } catch (error: any) {
      setModalOpen(true);
      if (error.status === RESPONSE_CODE.BAD_REQUEST) {
        setMessage(error.response.data);
        console.error("Status code:", error.response.status);
        console.error("Message:", error.response.data);
      } else {
        setMessage("認証コードの検証に失敗しました。");
        console.error("認証コードの検証に失敗しました。", error);
      }
    }
  };

  return (
    <>
      <MessageModal
        open={isModalOpen}
        message={message}
        handleClose={closeModal}
      />
      <ConfirmationModal
        open={confirmModalOpen}
        message={confirmMessage}
        handleClose={handleConfirmModalClose}
        onConfirm={onConfirm!}
      />
      {/* 認証用ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <Box sx={{ backgroundColor: theme.palette.custom.sForm }}>
          <DialogTitle>
            {verificationType === "email"
              ? "メール認証コード入力"
              : "電話番号認証コード入力"}
          </DialogTitle>
          <DialogContent>
            {verificationType === "email" && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                メールを確認し、4桁の認証コードを入力してください。
                <br />「{newEmail.mailAddress}
                」に数字4桁の認証コードをお送りしました。
              </Typography>
            )}
            {verificationType === "phone" && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                SMSを確認し、4桁の認証コードを入力してください。
                <br />「{newEmail.telNumber}
                」に数字4桁の認証コードをお送りしました。
              </Typography>
            )}
            <TextField
              autoFocus
              margin="dense"
              label="認証コード"
              type="text"
              fullWidth
              variant="outlined"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>キャンセル</Button>
            <Button onClick={handleSubmitVerification}>送信</Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Box>
        {/* 新規登録モーダル */}
        <SettingNewMailDialog
          open={open}
          handleClose={handleClose}
          handleAddEmail={handleAddEmail}
          newEmail={newEmail}
          setNewEmail={setNewEmail}
          handleVerify={handleVerify}
          isEmailVerified={isEmailVerified}
          isPhoneVerified={isPhoneVerified}
          message={dialogMessage}
          setMessage={setDialogMessage}
          newMailCheckboxChange={newMailCheckboxChange}
        />
        {isMd ? (
          <>
            {createButton()}
            <Box
              sx={{
                minHeight: tableBodyHeight,
                maxHeight: tableBodyHeight,
                overflowY: "auto",
              }}
            >
              <Grid2 container spacing={2}>
                {mailInfos.map((row, index) => (
                  <Grid2 size={{ xs: 12, sm: 6, md: 6 }} key={row.id}>
                    <SettingMailMobile
                      key={row.id}
                      row={row}
                      index={index}
                      width={cardBodyWidth}
                      isEditing={editingRows.has(index)}
                      currentData={rowEditData[index] || row}
                      handleChange={handleChange}
                      handleCheckboxChange={handleCheckboxChange}
                      handleSaveRow={handleSaveRow}
                      handleCancelRow={handleCancelRow}
                      handleDelete={handleDelete}
                      startEditingRow={startEditingRow}
                      openModal={handleConfirmModalOpen}
                    />
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          </>
        ) : (
          // PC
          <>
            <TableContainer
              component={Paper}
              sx={{
                minHeight: tableBodyHeight,
                maxHeight: tableBodyHeight,
                overflowY: "auto",
                backgroundColor: theme.palette.custom.row,
              }}
            >
              <Table
                sx={{
                  "& th, & td": {
                    textAlign: "center",
                    verticalAlign: "middle",
                  },
                  "& th": {
                    color: "#fff",
                  },
                }}
              >
                <TableHead
                  sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: theme.palette.custom.tableHeader,
                    zIndex: 1,
                  }}
                >
                  <TableRow
                    sx={{
                      "& > th": {
                        padding: "4px 8px 4px 8px",
                        height: "40px",
                      },
                    }}
                  >
                    <TableCell
                      rowSpan={3}
                      sx={{
                        width: mailColumnWidths.mailName,
                        whiteSpace: "nowrap",
                      }}
                    >
                      氏名
                    </TableCell>
                    <TableCell
                      rowSpan={3}
                      sx={{
                        width: mailColumnWidths.mailAddress,
                        whiteSpace: "nowrap",
                      }}
                    >
                      メールアドレス
                    </TableCell>
                    <TableCell
                      rowSpan={1}
                      colSpan={2}
                      sx={{
                        width: mailColumnWidths.mailAddress,
                        whiteSpace: "nowrap",
                      }}
                    >
                      メール配信
                    </TableCell>
                    <TableCell
                      rowSpan={3}
                      sx={{
                        width: mailColumnWidths.telNumber,
                        whiteSpace: "nowrap",
                      }}
                    >
                      電話番号
                    </TableCell>
                    <TableCell
                      rowSpan={1}
                      colSpan={1}
                      sx={{
                        width: mailColumnWidths.smsCheckbox,
                        whiteSpace: "nowrap",
                      }}
                    >
                      SMS配信
                    </TableCell>
                    <TableCell
                      rowSpan={3}
                      sx={{ width: mailColumnWidths.action }}
                    >
                      アクション
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      "& > th": {
                        borderBottom: "none",
                        padding: "0px",
                        height: "30px",
                        marginBottom: 0,
                        lineHeight: "30px",
                      },
                    }}
                  >
                    {/* 警報のみヘッダ */}
                    <TableCell
                      rowSpan={1}
                      sx={{
                        width: mailColumnWidths.alertCheckbox,
                      }}
                    >
                      警報
                    </TableCell>
                    <TableCell
                      rowSpan={1}
                      sx={{ width: mailColumnWidths.connectCheckbox }}
                    >
                      {/* 通信不可チェックボックス */}
                      通信不可
                    </TableCell>
                    <TableCell
                      rowSpan={1}
                      sx={{ width: mailColumnWidths.smsCheckbox }}
                    >
                      警報
                    </TableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      "& > th": {
                        padding: 0,
                        margin: 0,
                      },
                    }}
                  >
                    <TableCell
                      rowSpan={1}
                      sx={{ width: mailColumnWidths.alertCheckbox }}
                    >
                      <Checkbox
                        size="small"
                        checked={isAllAlertOnlyChecked}
                        indeterminate={
                          isSomeAlertOnlyChecked && !isAllAlertOnlyChecked
                        }
                        onChange={(e) =>
                          handleCheckAll("alertCheckbox", e.target.checked)
                        }
                        sx={{ marginTop: 0, paddingTop: 0 }}
                        color="default"
                        aria-label="警報チェックボックス"
                      />
                    </TableCell>
                    <TableCell
                      rowSpan={1}
                      sx={{ width: mailColumnWidths.connectCheckbox }}
                    >
                      <Checkbox
                        size="small"
                        checked={isAllConnectChecked}
                        indeterminate={
                          isSomeConnectChecked && !isAllConnectChecked
                        }
                        onChange={(e) =>
                          handleCheckAll("connectCheckbox", e.target.checked)
                        }
                        sx={{ marginTop: 0, paddingTop: 0 }}
                        color="default"
                        aria-label="通信不可チェックボックス"
                      />
                    </TableCell>
                    <TableCell
                      rowSpan={1}
                      sx={{ width: mailColumnWidths.smsCheckbox }}
                    >
                      <Checkbox
                        size="small"
                        checked={isAllSmsChecked}
                        indeterminate={isSomeSmsChecked && !isAllSmsChecked}
                        onChange={(e) =>
                          handleCheckAll("smsCheckbox", e.target.checked)
                        }
                        sx={{ marginTop: 0, paddingTop: 0 }}
                        color="default"
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mailInfos.map((row, index) => (
                    <SettingMailRow
                      key={row.id}
                      row={row}
                      index={index}
                      isEditing={editingRows.has(index)}
                      currentData={rowEditData[index] || row}
                      handleChange={handleChange}
                      handleCheckboxChange={handleCheckboxChange}
                      handleSaveRow={handleSaveRow}
                      handleCancelRow={handleCancelRow}
                      handleDelete={handleDelete}
                      startEditingRow={startEditingRow}
                      openModal={handleConfirmModalOpen}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {createButton()}
          </>
        )}
      </Box>
    </>
  );
};
export default SettingMail;
